/**
 * Página: Gerenciamento de Tenant (Admin Root)
 * Descrição: Tela exclusiva para Root Admin visualizar dados de qualquer tenant
 * 
 * Funcionalidades:
 * - Seletor de tenant para escolher qual workspace visualizar
 * - Todas as abas do gerenciamento: Overview, Billing, Users, Inboxes, Requests
 * 
 * @author Sistema
 * @update 2026-01-19 - Movido de /tenant para /admin com seletor de tenant
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  LayoutDashboard, 
  CreditCard, 
  Users, 
  Inbox, 
  Activity,
  Sparkles,
  RefreshCw,
  Loader2,
  Building2,
  Search,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAdminTenantManagement } from "@/hooks/useAdminTenantManagement";
import { OverviewTab } from "@/components/tenant/management/OverviewTab";
import { BillingTab } from "@/components/tenant/management/BillingTab";
import { UsersTab } from "@/components/tenant/management/UsersTab";
import { InboxesTab } from "@/components/tenant/management/InboxesTab";
import { RequestsTab } from "@/components/tenant/management/RequestsTab";
import { logger } from "@/lib/logger";

// ============================================================================
// COMPONENTE SELETOR DE TENANT
// ============================================================================

interface TenantOption {
  id: string;
  name: string;
  slug: string;
  plan: string | null;
  status: string;
}

/**
 * Seletor de tenant para o Root Admin escolher qual visualizar
 */
function TenantSelector({
  selectedTenantId,
  onSelect,
}: {
  selectedTenantId: string | null;
  onSelect: (tenantId: string) => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");

  // Buscar lista de todos os tenants
  const { data: tenants, isLoading } = useQuery({
    queryKey: ["admin-tenants-list"],
    queryFn: async () => {
      logger.debug("TenantSelector", "Buscando lista de tenants");

      const { data, error } = await supabase
        .from("tenants")
        .select("id, name, slug, plan, status")
        .order("name", { ascending: true });

      if (error) {
        logger.error("TenantSelector", "Erro ao buscar tenants", { error });
        throw error;
      }

      return (data || []) as unknown as TenantOption[];
    },
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // Filtrar tenants pela busca
  const filteredTenants = useMemo(() => {
    if (!tenants) return [];
    if (!searchQuery.trim()) return tenants;

    const query = searchQuery.toLowerCase();
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(query) ||
        t.slug.toLowerCase().includes(query)
    );
  }, [tenants, searchQuery]);

  // Tenant selecionado atual
  const selectedTenant = tenants?.find((t) => t.id === selectedTenantId);

  return (
    <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg border mb-6">
      <Building2 className="h-5 w-5 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <label className="text-sm font-medium text-muted-foreground">
          Selecione um Tenant
        </label>
        <Select
          value={selectedTenantId || ""}
          onValueChange={onSelect}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full mt-1">
            <SelectValue placeholder={isLoading ? "Carregando..." : "Escolha um tenant para visualizar"}>
              {selectedTenant && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{selectedTenant.name}</span>
                  <span className="text-xs text-muted-foreground">({selectedTenant.slug})</span>
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {/* Campo de busca dentro do dropdown */}
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar tenant..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8"
                />
              </div>
            </div>
            {/* Lista de tenants filtrados */}
            {filteredTenants.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {isLoading ? "Carregando tenants..." : "Nenhum tenant encontrado"}
              </div>
            ) : (
              filteredTenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{tenant.name}</span>
                    <span className="text-xs text-muted-foreground">({tenant.slug})</span>
                    {tenant.status !== "active" && (
                      <Badge variant="secondary" className="text-xs">
                        {tenant.status}
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTE HEADER PREMIUM
// ============================================================================

/**
 * Header decorativo com gradiente e efeitos
 */
const PremiumHeader = ({
  title,
  description,
  tenantName,
  planBadge,
  onSync,
  isSyncing,
}: {
  title: string;
  description: string;
  tenantName?: string;
  planBadge?: { label: string; variant: "success" | "warning" | "destructive" | "secondary" };
  onSync?: () => void;
  isSyncing?: boolean;
}) => (
  <div className="relative overflow-hidden rounded-2xl border bg-card mb-8">
    {/* Background decorativo com gradiente */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
    <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
    <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
    
    {/* Borda gradient no topo */}
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
    
    <div className="relative p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* Ícone decorativo */}
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Sparkles className="h-6 w-6" />
          </div>
          
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
              {planBadge && (
                <Badge 
                  variant={planBadge.variant === "success" || planBadge.variant === "warning" ? "secondary" : planBadge.variant}
                  className={cn(
                    "px-3 py-1",
                    planBadge.variant === "success" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0",
                    planBadge.variant === "warning" && "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0"
                  )}
                >
                  {planBadge.label}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1">
              {description}
            </p>
            {tenantName && (
              <p className="text-sm text-muted-foreground/80 mt-0.5">
                Tenant: <span className="font-medium text-foreground">{tenantName}</span>
              </p>
            )}
          </div>
        </div>
        
        {/* Botão de sincronização */}
        {onSync && (
          <Button
            variant="outline"
            size="sm"
            onClick={onSync}
            disabled={isSyncing}
            className="self-start sm:self-center"
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sincronizar
          </Button>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// COMPONENTE PLACEHOLDER PARA ABAS FUTURAS
// ============================================================================

const PlaceholderTab = ({ title, description }: { title: string; description: string }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
      <Activity className="h-8 w-8 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-muted-foreground max-w-md">
      {description}
    </p>
    <Badge variant="secondary" className="mt-4">
      Em desenvolvimento
    </Badge>
  </div>
);

// ============================================================================
// COMPONENTE ESTADO VAZIO (NENHUM TENANT SELECIONADO)
// ============================================================================

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-6">
      <Building2 className="h-10 w-10 text-muted-foreground" />
    </div>
    <h3 className="text-xl font-semibold mb-2">Selecione um Tenant</h3>
    <p className="text-muted-foreground max-w-md">
      Escolha um tenant no seletor acima para visualizar seus dados de gerenciamento,
      incluindo métricas, faturamento, usuários e inboxes.
    </p>
  </div>
);

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function AdminTenantManagement() {
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Hook adaptado para receber tenantId externo
  const {
    data,
    billing,
    timeSeries,
    resourceLimits,
    isLoading,
    syncSubscription,
    isSyncingSubscription,
  } = useAdminTenantManagement(selectedTenantId);

  // Badge do plano para o header
  const getPlanBadge = () => {
    if (!selectedTenantId || !data) return undefined;
    
    if (data.billing.isInTrial) {
      return { label: `Trial • ${data.billing.trialDaysRemaining} dias`, variant: "warning" as const };
    }
    if (data.billing.subscriptionStatus === "active") {
      return { label: data.billing.planName, variant: "success" as const };
    }
    if (data.billing.subscriptionStatus === "past_due") {
      return { label: "Pagamento Pendente", variant: "destructive" as const };
    }
    return { label: data.billing.planName, variant: "success" as const };
  };

  // Definição das tabs
  const tabs = [
    { id: "overview", label: "Visão Geral", icon: LayoutDashboard },
    { id: "billing", label: "Faturamento", icon: CreditCard },
    { id: "users", label: "Usuários", icon: Users },
    { id: "inboxes", label: "Inboxes", icon: Inbox },
    { id: "requests", label: "Requisições", icon: Activity },
  ];

  // Preparar dados de add-ons para BillingTab (usando nova estrutura TenantAddonDetails)
  // IMPORTANTE: Incluir addon_type para renderização condicional por tipo
  const tenantAddonsForBilling = resourceLimits.tenantAddons?.map((addon) => ({
    id: addon.id,
    name: addon.addon_name || "Add-on",
    addon_type: addon.addon_type, // Tipo do add-on (inbox, agent, module, channel)
    quantity: addon.quantity,
    status: addon.status,
    price_monthly: addon.price_monthly || 0,
    provider_name: addon.provider_name,
    provider_slug: addon.provider_slug,
    current_period_end: addon.current_period_end,
    stripe_price_id: addon.stripe_price_id, // NOVO: ID do preço no Stripe para vincular histórico
    linked_inboxes: addon.linked_inboxes || [],
  })) || [];

  return (
    <div className="container mx-auto py-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
      {/* Seletor de Tenant */}
      <TenantSelector
        selectedTenantId={selectedTenantId}
        onSelect={setSelectedTenantId}
      />

      {/* Conteúdo condicional baseado na seleção */}
      {!selectedTenantId ? (
        <EmptyState />
      ) : (
        <>
          {/* Header Premium */}
          <PremiumHeader
            title="Gerenciamento de Tenant"
            description="Visão completa do workspace: plano, usuários, inboxes e métricas"
            tenantName={data?.tenant?.name}
            planBadge={getPlanBadge()}
            onSync={syncSubscription}
            isSyncing={isSyncingSubscription}
          />

          {/* Sistema de Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex items-center gap-2 data-[state=active]:bg-background"
                >
                  <tab.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Conteúdo das Tabs */}
            <TabsContent value="overview" className="mt-6">
              <OverviewTab 
                data={data} 
                messagesByDay={timeSeries.data?.messagesByDay}
                isLoading={isLoading || timeSeries.isLoading}
              />
            </TabsContent>

            <TabsContent value="billing" className="mt-6">
              <BillingTab
                data={data}
                billingHistory={(billing.billingHistory || []) as any}
                tenantAddons={tenantAddonsForBilling}
                tenantId={selectedTenantId || undefined} // NOVO: Passa ID do tenant para histórico de add-on
                onManageBilling={() => {}} // Admin não gerencia billing diretamente
                onSync={syncSubscription}
                isSyncing={isSyncingSubscription}
                isLoading={isLoading || billing.historyLoading}
              />
            </TabsContent>

            <TabsContent value="users" className="mt-6">
              <UsersTab
                members={data?.members || []}
                maxAgents={data?.limits.maxAgents || 0}
                isEnterprise={data?.limits.isEnterprise || false}
                isLoading={isLoading}
              />
            </TabsContent>

            <TabsContent value="inboxes" className="mt-6">
              <InboxesTab
                inboxes={data?.inboxes || []}
                maxInboxes={data?.limits.maxInboxes || 0}
                isEnterprise={data?.limits.isEnterprise || false}
                isLoading={isLoading}
              />
            </TabsContent>

            <TabsContent value="requests" className="mt-6">
              <RequestsTab 
                tenantId={selectedTenantId!}
                isLoading={isLoading}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
